import type { ResultOf } from '@graphql-typed-document-node/core'
import { min, sortBy } from 'lodash-es'
import type { RestaurantSearchCourse } from '~/components/course/RestaurantSearchCourse.frag'
import { PlanSearchPrice } from '~/components/plan/PlanSearchPrice.frag'
import { PlanSelectionInfo } from '~/components/plan/PlanSelectionInfo.frag'
import { getFragmentData } from '~/lib/graphql'
import type { MealPeriod } from '~/lib/graphql/graphql'
import { type MealPeriodTab, mealPeriodTabs } from '~/lib/mealPeriod/tab'

/**
 * コースオブジェクトの中のPlanを指定されたMealPeriod別にフィルタリングする
 * @param courses
 * @param mealPeriod
 * @returns フィルタリングされたCourseList（Array）
 */
const filterPlanByMealPeriod = (
  courses: ResultOf<typeof RestaurantSearchCourse>[],
  mealPeriod: MealPeriod
): ResultOf<typeof RestaurantSearchCourse>[] => {
  return courses.flatMap((course) => {
    const edges = course.searchPlans.edges.filter((edge) => {
      const plan = getFragmentData(PlanSelectionInfo, edge.node)
      const price = getFragmentData(PlanSearchPrice, edge.node.searchPrice)
      if (!price?.hasInventory) {
        return false
      }
      return plan.mealPeriod === mealPeriod
    })
    return edges.length > 0
      ? {
          ...course,
          searchPlans: {
            ...course.searchPlans,
            edges,
          },
        }
      : []
  })
}

/**
 * コースに紐づくプランの中で最安の料金を取得する
 * @param course
 * @returns コースの最安料金
 */
const getMinBestPrice = (course: ResultOf<typeof RestaurantSearchCourse>): number | undefined => {
  return min(
    course.searchPlans.edges.map(
      (edge) => getFragmentData(PlanSearchPrice, edge.node.searchPrice)?.bestPrice
    )
  )
}

/**
 * コースのリストをタブに表示する時間帯(MealPeriodTab)別にグループ化して返す
 * コースが存在しない時間帯は除外する
 * @param courses
 * @returns 各時間帯のMealPeriodTabとコースのリストを内包した配列
 */
export const groupCoursesByMealPeriodTab = (
  courses: ResultOf<typeof RestaurantSearchCourse>[]
): [MealPeriodTab, ResultOf<typeof RestaurantSearchCourse>[]][] => {
  return mealPeriodTabs.flatMap((tab) => {
    // Courseの中に含まれているPlanを各MealPeriodでフィルタリングし、Courseを安い順にソートする
    const coursesForTab = sortBy(filterPlanByMealPeriod(courses, tab), (course) =>
      getMinBestPrice(course)
    )
    return coursesForTab.length > 0 ? [[tab, coursesForTab]] : []
  })
}

/**
 * 席のみプランのリストをタブに表示する時間帯(MealPeriodTab)別にグループ化して返す
 * 席のみプランが存在しない時間帯は除外する
 * @param seatOnlyPlans
 * @returns 各時間帯のMealPeriodTabと席のみプランのリストを内包した配列
 */
export const groupSeatOnlyPlansByMealPeriodTab = <P extends ResultOf<typeof PlanSelectionInfo>>(
  seatOnlyPlans: P[]
): [MealPeriodTab, P[]][] => {
  return mealPeriodTabs.flatMap((tab) => {
    const plans = seatOnlyPlans.filter((plan) => plan.mealPeriod === tab)

    return plans.length > 0 ? [[tab, plans]] : []
  })
}
